import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Destroyable } from '@dlw-int-mwi/eps-common';
import { TranslateService } from '@ngx-translate/core';
import { fadeInAnimation } from 'app/animations';
import { appSettings } from 'app/appSettings';
import { TranslationHelperService } from 'app/core/services/translate-helper.service';
import { ValidatorService } from 'app/core/services/validator.service';
import { OrderDetail, RECURRENCE_PATTERN_NR_OF_DAY, RECURRENCE_PATTERN_WEEKLY, RECURRENCE_PATTERN_WHICH_DAY } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { util } from 'app/core/util/util';
import { Definition } from '../../../core/store/definitions/definition.interface';
import { DatesRange } from '../../../core/util/date.utils';
import { LayoutBreakpoints } from '../../../core/util/layout-breakpoints';

export function getRecurrenceFormGroup(orderDetail: OrderDetail, formBuilder: UntypedFormBuilder, validatorService: ValidatorService): UntypedFormGroup {
  const control = formBuilder.group({
    active: [orderDetail.planning.recurrence?.active],
    pattern: [orderDetail.planning.recurrence?.pattern],
    endDate: [orderDetail.planning.recurrence?.endDate],
    weekly: formBuilder.group({
      monday: [orderDetail.planning.recurrence?.weekly.monday],
      tuesday: [orderDetail.planning.recurrence?.weekly.tuesday],
      wednesday: [orderDetail.planning.recurrence?.weekly.wednesday],
      thursday: [orderDetail.planning.recurrence?.weekly.thursday],
      friday: [orderDetail.planning.recurrence?.weekly.friday],
      saturday: [orderDetail.planning.recurrence?.weekly.saturday],
      sunday: [orderDetail.planning.recurrence?.weekly.sunday],
      mondayRecurrence: [orderDetail.planning.recurrence?.weekly.mondayRecurrence || 1],
      tuesdayRecurrence: [orderDetail.planning.recurrence?.weekly.tuesdayRecurrence || 1],
      wednesdayRecurrence: [orderDetail.planning.recurrence?.weekly.wednesdayRecurrence || 1],
      thursdayRecurrence: [orderDetail.planning.recurrence?.weekly.thursdayRecurrence || 1],
      fridayRecurrence: [orderDetail.planning.recurrence?.weekly.fridayRecurrence || 1],
      saturdayRecurrence: [orderDetail.planning.recurrence?.weekly.saturdayRecurrence || 1],
      sundayRecurrence: [orderDetail.planning.recurrence?.weekly.sundayRecurrence || 1],
    }),
    monthly: formBuilder.group({
      day: [orderDetail.planning.recurrence?.monthly.day],
      monthPeriodicy1: [orderDetail.planning.recurrence?.monthly.monthPeriodicy1],
      monthPeriodicy2: [orderDetail.planning.recurrence?.monthly.monthPeriodicy2],
      which: [orderDetail.planning.recurrence?.monthly.which],
      weekday: [orderDetail.planning.recurrence?.monthly.weekday],
    }),
  });

  const checkboxCheckedCountValidator = (control: AbstractControl): { [key: string]: boolean } => {
    const oneIsChecked = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']
      .map(val => val.toLowerCase())
      .map(val => control.get(val).value && control.get(val + 'Recurrence').value > 0)
      .reduce((prev, cur) => prev || cur, false);
    if (oneIsChecked) {
      return null;
    } else {
      return { NONE_CHECKED: true };
    }
  };

  const weeklyCondition = util.createCondition(() => control.controls.pattern, RECURRENCE_PATTERN_WEEKLY);
  const recurrenceCondition = util.createCondition(() => control.controls.active);
  util.conditionalValidation(control, () => control.controls.endDate, [recurrenceCondition], [Validators.required, validatorService.datePatternValidator]);
  util.conditionalValidation(control, () => control.controls.weekly, [weeklyCondition, recurrenceCondition], [checkboxCheckedCountValidator]);

  return control;
}

export function updateRecurrenceFormGroup(control: UntypedFormGroup, orderDetail: OrderDetail) {
  control.patchValue({
    active: orderDetail.planning.recurrence?.active,
    pattern: orderDetail.planning.recurrence?.pattern,
    endDate: orderDetail.planning.recurrence?.endDate,
  });
  const weeklyControl = control.get(RECURRENCE_PATTERN_WEEKLY) as UntypedFormGroup;
  weeklyControl.patchValue({
    monday: orderDetail.planning.recurrence?.weekly.monday,
    tuesday: orderDetail.planning.recurrence?.weekly.tuesday,
    wednesday: orderDetail.planning.recurrence?.weekly.wednesday,
    thursday: orderDetail.planning.recurrence?.weekly.thursday,
    friday: orderDetail.planning.recurrence?.weekly.friday,
    saturday: orderDetail.planning.recurrence?.weekly.saturday,
    sunday: orderDetail.planning.recurrence?.weekly.sunday,
  });
  const monthlyControl = control.get('monthly') as UntypedFormGroup;
  monthlyControl.patchValue({
    day: orderDetail.planning.recurrence?.monthly.day,
    monthPeriodicy1: orderDetail.planning.recurrence?.monthly.monthPeriodicy1,
    monthPeriodicy2: orderDetail.planning.recurrence?.monthly.monthPeriodicy2,
    which: orderDetail.planning.recurrence?.monthly.which,
    weekday: orderDetail.planning.recurrence?.monthly.weekday,
  });
}

@Component({
  selector: 'app-florder-recurrence',
  templateUrl: './florder-recurrence.component.html',
  styleUrls: ['./florder-recurrence.component.scss'],
  animations: [fadeInAnimation],
})
export class FlorderRecurrenceComponent extends Destroyable implements OnInit, OnChanges {
  @Input() form: UntypedFormGroup;
  @Input() isEdit: boolean;
  @Input() loadingDate: string;

  labelWidth = appSettings.LABEL_WIDTH;
  ordinalEnum: { id: string; name: string }[] = [];
  weekdayEnum: { id: string; name: string }[] = [];

  route: string = '';
  isRelocation: boolean = false;
  endDateValidRange: DatesRange;
  recurrencyPatternOptions: Definition[] = [
    { id: RECURRENCE_PATTERN_WEEKLY, name: 'ORDERS.DELIVERY.LABELS.PATTERN_WEEKLY' },
    { id: RECURRENCE_PATTERN_NR_OF_DAY, name: 'ORDERS.DELIVERY.LABELS.PATTERN_NROFDAY' },
    { id: RECURRENCE_PATTERN_WHICH_DAY, name: 'ORDERS.DELIVERY.LABELS.PATTERN_WHICHDAY' }
  ];

  RECURRENCE_PATTERN_WEEKLY = RECURRENCE_PATTERN_WEEKLY;
  RECURRENCE_PATTERN_WHICH_DAY = RECURRENCE_PATTERN_WHICH_DAY;
  RECURRENCE_PATTERN_NR_OF_DAY = RECURRENCE_PATTERN_NR_OF_DAY;
  private _recurrencyPattern: string = '';

  isExtraSmall$ = LayoutBreakpoints.matches(this.breakpointObserver, LayoutBreakpoints.extraSmallScreenOrLess);

  constructor(private translationHelper: TranslationHelperService, private translate: TranslateService, private router: Router, private breakpointObserver: BreakpointObserver) {
    super();
  }

  ngOnInit(): any {
    this.ordinalEnum = this.translationHelper.translateArrayInstant(
      ['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'LAST'],
      (value, translation) => {
        return { id: value, name: translation };
      },
      'SHARED.ORDINAL.'
    );
    this.weekdayEnum = this.translationHelper.translateArrayInstant(
      ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
      (value, translation) => {
        return { id: value, name: translation };
      },
      'SHARED.DAYS.'
    );

    this.route = this.router.url;
    this.isRelocation = this.route.toLowerCase().includes('relocations');
    this.form.valueChanges.pipe(this.takeUntilDestroyed()).subscribe(() => this._recurrencyPattern = null);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.loadingDate) {
      let startDate: string = null;
      if (this.loadingDate) {
        startDate = util.parseStringToMoment(this.loadingDate).add(1, 'day').format(appSettings.DATE_FORMAT_MOMENT_REST);
      }
      this.endDateValidRange = {
        startDate,
        endDate: null
      }
    }
  }
  get recurrencyPattern(): string {
    if (!this._recurrencyPattern) {
      this.renderRecurrencyPattern();
    }
    return this._recurrencyPattern;
  }

  private renderRecurrencyPattern(): void {
    if (this.form.value.pattern === RECURRENCE_PATTERN_WEEKLY) {
      let days;
      const weekly = this.form.value.weekly;
      if (!this.isRelocation) {
        const controlsArray = {
          MONDAY: weekly.monday && weekly.mondayRecurrence || 0,
          TUESDAY: weekly.tuesday && weekly.tuesdayRecurrence || 0,
          WEDNESDAY: weekly.wednesday && weekly.wednesdayRecurrence || 0,
          THURSDAY: weekly.thursday && weekly.thursdayRecurrence || 0,
          FRIDAY: weekly.friday && weekly.fridayRecurrence || 0,
          SATURDAY: weekly.saturday && weekly.saturdayRecurrence || 0,
          SUNDAY: weekly.sunday && weekly.sundayRecurrence || 0,
        };
        const selectedNonEmptyDays = this.weekdayEnum.filter(day => weekly[day.id.toLowerCase()] && weekly[`${day.id.toLowerCase()}Recurrence`] > 0);
        const selectedNonEmptyDayStrings = [];
        for (let index = 0; index < selectedNonEmptyDays.length; index++) {
          const day = selectedNonEmptyDays[index];
          selectedNonEmptyDayStrings.push(`${day.name} (${controlsArray[day.id]})`);
        }
        days = selectedNonEmptyDayStrings.join(', ');
      } else {
        days = this.weekdayEnum
          .filter(day => weekly[day.id.toLowerCase()])
          .map(day => day.name)
          .join(', ');
      }

      this._recurrencyPattern = this.translate.instant(`ORDERS.DELIVERY.VALUES.RECURRENCY.WEEKLY`, { days });
    } else {
      const label = this.translate.instant(`ORDERS.DELIVERY.VALUES.RECURRENCY.MONTHLY-${this.form.value.pattern}`.toUpperCase(), this.form.value.monthly);
      this._recurrencyPattern = label.substring(0, 1) + label.toLowerCase().substring(1);
    }
  }

  get endPattern(): string {
    return this.translate.instant(`ORDERS.DELIVERY.VALUES.RECURRENCY.UNTIL`, { endDate: util.localizeDateString(this.form.value.endDate) });
  }
}
